// ITER289 — Premium Loyalty Theme Provider.
// ITER294 — Brand-driven theming (no hardcoded Royal Blue when a
// tenant has configured colours). Priority cascade:
//   Membership/Voucher theme (per-voucher overrides — future)
//        ↓
//   Brand / Outlet config (theme_primary + theme_secondary + logo_url)
//        ↓
//   Tenant defaults (future — currently the same shape as outlet cfg)
//        ↓
//   System default (Royal Blue + Gold — The Bell Tower reference)
//
// The provider takes a `config` prop that mirrors the shape returned
// by `GET /loyalty/guest/outlet-by-slug/:slug`. Missing / blank fields
// gracefully fall through to defaults. Everything downstream reads
// CSS variables — no component should ever hardcode a hex.
//
// ─────────────────────────────────────────────────────────────────
// STANDING RULES FOR EVERY GUEST-FACING PAGE
// (applies to /hub/:slug, /hub/:slug/membership, /menu/:slug,
//  /feedback/:outletId/:formType, and every future public surface)
//
//   • Mobile is the primary viewport, not a fallback. Design at
//     390×844 first; desktop is the adaptation.
//   • Content fills the width. 16–20px side padding. Never a narrow
//     centred column with large empty margins on a phone.
//   • The page fills the viewport height. Background always covers
//     the full screen (`min-h-screen` on the outer wrapper; the
//     wrapper's `background: var(--canvas)` reaches every pixel).
//   • Type is sized for a phone held at arm's length. Body text
//     never below 15px. Headings substantially larger than body.
//   • Every card has a visible edge — accent-tinted border, or a
//     clear tonal separation from the background.
//   • Touch targets ≥ 48px tall with ≥ 12px between them.
//   • The outlet's theme drives the palette. Primary, secondary and
//     background come from the outlet's saved theme. Never hardcode
//     a colour that should be themed.
// ─────────────────────────────────────────────────────────────────

import React from 'react';

/* ==============================================================
   System default palette (Royal Blue + Gold — The Bell Tower)
   ============================================================== */
export const DEFAULT_LOYALTY_THEME = {
  canvas:       '#FFFFFF',
  surface:      '#FAFAFA',
  primary:      '#0B2A5B',
  primaryDeep:  '#071C3D',
  primarySoft:  '#EEF2F6',
  gold:         '#C9A34E',
  goldSoft:     '#F4EFE6',
  accent:       '#C9A34E',
  accentSoft:   '#F4EFE6',
  ink:          '#111827',
  inkMuted:     '#6B7280',
  onPrimary:    '#FFFFFF',
  borderSubtle: '#F3F4F6',
  borderDefault:'#E5E7EB',
  shadowSm:     '0 2px 8px rgba(0, 0, 0, 0.04)',
  shadowMd:     '0 8px 24px rgba(0, 0, 0, 0.06)',
  shadowLg:     '0 16px 40px rgba(11, 42, 91, 0.08)',
  shadowHero:   '0 24px 48px rgba(11, 42, 91, 0.12)',
  gradientCard: 'linear-gradient(135deg, #0B2A5B 0%, #071C3D 100%)',
  gradientHero: 'linear-gradient(135deg, #0B2A5B 0%, #071C3D 100%)',
  watermark:    'rgba(255, 255, 255, 0.06)',
  patternColor: 'rgba(201, 163, 78, 0.10)',
  statusUnusedBg:  '#F4EFE6', statusUnusedFg:  '#C9A34E',
  statusReservedBg:'#EEF2F6', statusReservedFg:'#0B2A5B',
  statusRedeemedBg:'#F3F4F6', statusRedeemedFg:'#6B7280',
  statusExpiredBg: '#F9FAFB', statusExpiredFg: '#9CA3AF',
};

/* ==============================================================
   Colour helpers — hex ⇄ HSL, luminance, deepen, soften.
   All pure functions, no dependencies.
   ============================================================== */
const _norm = (h) => {
  if (!h || typeof h !== 'string') return null;
  const s = h.trim().replace(/^#/, '');
  if (!/^([0-9a-f]{3}|[0-9a-f]{6})$/i.test(s)) return null;
  return '#' + (s.length === 3 ? s.split('').map((c) => c + c).join('') : s).toLowerCase();
};
const _hexToRgb = (h) => {
  const n = _norm(h); if (!n) return null;
  return { r: parseInt(n.slice(1, 3), 16),
           g: parseInt(n.slice(3, 5), 16),
           b: parseInt(n.slice(5, 7), 16) };
};
const _rgbToHex = ({ r, g, b }) =>
  '#' + [r, g, b].map((x) => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, '0')).join('');
const _mix = (a, b, t) => {
  const A = _hexToRgb(a), B = _hexToRgb(b);
  if (!A || !B) return a;
  return _rgbToHex({ r: A.r + (B.r - A.r) * t,
                     g: A.g + (B.g - A.g) * t,
                     b: A.b + (B.b - A.b) * t });
};
const _luminance = (hex) => {
  const c = _hexToRgb(hex); if (!c) return 0.5;
  const [R, G, B] = [c.r, c.g, c.b].map((v) => {
    v = v / 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
};
const _isDark = (hex) => _luminance(hex) < 0.45;
const _rgba = (hex, a) => {
  const c = _hexToRgb(hex); if (!c) return `rgba(0,0,0,${a})`;
  return `rgba(${c.r}, ${c.g}, ${c.b}, ${a})`;
};

/* ==============================================================
   Derive a full palette from (primary, secondary, background).
   Everything else is computed so the admin only ever configures
   the two brand hexes.
   ============================================================== */
export function derivePalette(config = {}) {
  const primary   = _norm(config.theme_primary)    || DEFAULT_LOYALTY_THEME.primary;
  const secondary = _norm(config.theme_secondary)  || DEFAULT_LOYALTY_THEME.gold;
  const canvas    = _norm(config.theme_background) || DEFAULT_LOYALTY_THEME.canvas;

  const primaryDeep = _mix(primary, '#000000', 0.28);
  const primarySoft = _mix(primary, '#FFFFFF', 0.90);
  const accent      = secondary;
  const accentSoft  = _mix(secondary, '#FFFFFF', 0.86);
  const onPrimary   = _isDark(primary) ? '#FFFFFF' : '#111827';
  const shadowKey   = _rgba(primary, 0.12);
  const shadowHeroC = _rgba(primary, 0.18);

  return {
    ...DEFAULT_LOYALTY_THEME,
    canvas,
    surface: _mix(canvas, primary, 0.02),
    primary,
    primaryDeep,
    primarySoft,
    gold: secondary,
    goldSoft: accentSoft,
    accent,
    accentSoft,
    onPrimary,
    shadowLg:   `0 16px 40px ${shadowKey}`,
    shadowHero: `0 24px 48px ${shadowHeroC}`,
    gradientCard: `linear-gradient(135deg, ${primary} 0%, ${primaryDeep} 100%)`,
    gradientHero: `linear-gradient(135deg, ${primary} 0%, ${primaryDeep} 100%)`,
    watermark: _rgba(secondary, 0.10),
    patternColor: _rgba(secondary, 0.10),
    statusUnusedBg: accentSoft,   statusUnusedFg: accent,
    statusReservedBg: primarySoft, statusReservedFg: primary,
  };
}

/* ==============================================================
   Map JS token names → CSS variable names.
   ============================================================== */
function toCssVars(t) {
  return {
    '--canvas':          t.canvas,
    '--surface':         t.surface,
    '--primary':         t.primary,
    '--primary-deep':    t.primaryDeep,
    '--primary-soft':    t.primarySoft,
    '--gold':            t.gold,
    '--gold-soft':       t.goldSoft,
    '--accent':          t.accent,
    '--accent-soft':     t.accentSoft,
    '--on-primary':      t.onPrimary,
    '--ink':             t.ink,
    '--ink-muted':       t.inkMuted,
    '--border-subtle':   t.borderSubtle,
    '--border-default':  t.borderDefault,
    '--shadow-sm':       t.shadowSm,
    '--shadow-md':       t.shadowMd,
    '--shadow-lg':       t.shadowLg,
    '--shadow-hero':     t.shadowHero,
    '--gradient-card':   t.gradientCard,
    '--gradient-hero':   t.gradientHero,
    '--watermark':       t.watermark,
    '--pattern-color':   t.patternColor,
    '--status-unused':      t.statusUnusedFg,
    '--status-unused-bg':   t.statusUnusedBg,
    '--status-reserved':    t.statusReservedFg,
    '--status-reserved-bg': t.statusReservedBg,
    '--status-redeemed':    t.statusRedeemedFg,
    '--status-redeemed-bg': t.statusRedeemedBg,
    '--status-expired':     t.statusExpiredFg,
    '--status-expired-bg':  t.statusExpiredBg,
    // ITER305-refine · font-family is now handled by the scoped
    // <style> block in GuestLoyaltyWallet (Inter body + Playfair /
    // Montserrat / JetBrains via CSS class selectors). Inline
    // fontFamily used to defeat those rules, so removed here.
  };
}

/* ==============================================================
   ITER305 · Dark-mode overlay.
   The guest wallet is a premium hospitality experience — dark navy
   / gold, never white. We compute a dark canvas + surface derived
   from the tenant's own primary colour so multi-brand tenants still
   look on-brand while satisfying the "no white areas" requirement.
   ============================================================== */
function darkOverlay(palette) {
  const canvas       = palette.primaryDeep;
  const surface      = _mix(palette.primary, '#FFFFFF', 0.06);
  const ink          = '#F5F1E4';
  const inkMuted     = 'rgba(245,241,228,0.65)';
  const borderDefault = 'rgba(255,255,255,0.14)';
  const borderSubtle  = 'rgba(255,255,255,0.08)';
  return {
    canvas,
    surface,
    ink,
    inkMuted,
    borderDefault,
    borderSubtle,
    onPrimary: '#FFFFFF',
    statusUnusedBg:  _rgba(palette.gold, 0.14),   statusUnusedFg:  palette.gold,
    statusReservedBg:'rgba(255,255,255,0.08)',    statusReservedFg:'#FFFFFF',
    statusRedeemedBg:'rgba(255,255,255,0.05)',    statusRedeemedFg:'rgba(255,255,255,0.55)',
    statusExpiredBg: 'rgba(255,255,255,0.04)',    statusExpiredFg: 'rgba(255,255,255,0.45)',
  };
}

/**
 * Guest theme provider.
 * Priority: `config` (from outlet-by-slug) → `overrides` (per-tenant
 * hard set — future) → system default.
 * `mode="dark"` (ITER305) flips canvas/surface/ink to a dark navy
 * palette so the entire guest experience matches the premium Home
 * tab — no white areas across Home / Privileges / Card / History /
 * Profile.
 */
export function GuestLoyaltyThemeProvider({ config, overrides, mode,
                                             children, className, ...rest }) {
  const theme = React.useMemo(() => {
    const base = derivePalette(config || {});
    const dark = mode === 'dark' ? darkOverlay(base) : {};
    return { ...base, ...dark, ...(overrides || {}) };
  }, [config, overrides, mode]);
  return (
    <div
      className={className}
      style={{ ...toCssVars(theme), background: 'var(--canvas)', color: 'var(--ink)' }}
      data-loyalty-theme-root=""
      data-loyalty-mode={mode || 'light'}
      {...rest}
    >
      {/*
         Shared shimmer keyframes for guest surfaces. Any child can opt
         in with class `guest-shimmer`. Sweep is 3s, low opacity, and
         disabled automatically when the user prefers reduced motion.
      */}
      <style>{`
        @keyframes guest-shimmer-sweep {
          0%   { transform: translateX(-140%) rotate(12deg); opacity: 0; }
          40%  { opacity: 0.55; }
          100% { transform: translateX(140%) rotate(12deg); opacity: 0; }
        }
        .guest-shimmer { position: relative; overflow: hidden; isolation: isolate; }
        .guest-shimmer::after {
          content: '';
          position: absolute; inset: 0;
          background: linear-gradient(
            110deg,
            transparent 20%,
            ${theme.gold || '#C9A34E'}55 45%,
            ${theme.gold || '#C9A34E'}22 55%,
            transparent 80%
          );
          pointer-events: none;
          animation: guest-shimmer-sweep 3s ease-in-out infinite;
          animation-delay: 0.6s;
          mix-blend-mode: screen;
          z-index: 1;
        }
        @media (prefers-reduced-motion: reduce) {
          .guest-shimmer::after { animation: none; opacity: 0; }
        }
      `}</style>
      {children}
    </div>
  );
}

/* ==============================================================
   Shared type ramp — screens should not drift from these.
   ============================================================== */
export const T = {
  display:      'font-serif text-4xl leading-tight tracking-tight',
  h1:           'font-serif text-3xl leading-snug tracking-tight',
  h2:           'font-serif text-2xl leading-snug',
  h3:           'font-sans text-xl font-medium tracking-tight',
  body:         'font-sans text-base leading-relaxed',
  bodyMuted:    'font-sans text-base leading-relaxed',
  label:        'font-sans text-[10px] font-semibold uppercase tracking-[0.22em]',
  cardNumber:   'font-sans text-lg md:text-xl font-medium tracking-[0.28em]',
};
